import {User} from '../dto/User';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Configuration} from '../../core/common/configuration';
import {RoutingService} from '../../core/common/routing.service';
import {Subscription, throwError} from 'rxjs';
import {UtilsService} from '../../core/common/utils/utils.service';
import {StoreService} from '../../core/common/store/store.service';
import {SettingService} from '../../core/common/settings.service';
import {UserRestService} from './user-rest.service';
import {MenuService} from '../../core/menu/menu.service';
import {ToastService} from '../../core/toaster/toast.service';

export abstract class UserService<U extends User> {

  protected abstract user: U;
  protected abstract config: Configuration;
  protected abstract routing: RoutingService;
  protected abstract utils: UtilsService;
  protected abstract store: StoreService;
  protected abstract settings: SettingService;
  protected abstract restService: UserRestService<U>;
  protected abstract menuService: MenuService;
  protected abstract toastService: ToastService;

  getUser(): U {
    return this.user;
  }

  setUser(user: U | null): void {
    this.user = this.newUserInstance();
    if (user) {
      this.user.setFromObject(user);
    }
  }

  isValidUser(): boolean {
    return this.user.permissions.length > 0 &&
      this.user.username != null &&
      this.user.username.length > 0;
  }

  controlUser(): void {
    if (!this.isValidUser()) {
      this.restService.getUser()
        .subscribe(
          data => {
            this.configureUserApp(data);
          });
    }
  }

  refreshUser(): void {
    this.restService.getUser()
      .subscribe(
        data => {
          this.configureUserApp(data);
        });
  }

  quit(): void {
    this.settings.enableLoader();
    this.restService.quit()
      .subscribe({
        next: () => {
          this.store.deleteAll();
          this.settings.getError().code = null;
          this.settings.disableLoader();
          this.routing.navigateTo('/login');
        },
        error: () => {
          this.store.deleteAll();
          this.settings.getError().code = null;
          this.settings.disableLoader();
          this.routing.navigateTo('/login');
        }
      });
  }

  lock(): void {
    this.settings.enableLoader();
    this.restService.lock()
      .subscribe({
        next: () => {
          this.store.deleteToSave();
          this.settings.getError().code = null;
          this.settings.disableLoader();
          this.routing.navigateTo('/lock');
        },
        error: () => {
          this.store.deleteToSave();
          this.settings.getError().code = null;
          this.settings.disableLoader();
          this.routing.navigateTo('/lock');
        }
      });
  }

  login(username: string, password: string): Subscription {
    this.settings.enableLoader();
    this.setUser(null);
    this.store.deleteAll();
    return this.restService.login(username, password)
      .subscribe({
        next: data => {
          if (data.status === 200) {
            if (this.store.isValidTwoFaRequest()) {
              this.settings.disableLoader();
              this.routing.navigateTo('/authentication');
            } else {
              this.restService.getUser()
                .subscribe(
                  user => {
                    this.loginSubscribe(user);
                    this.settings.disableLoader();
                  });
            }
          }
        },
        error: error => {
          this.dispatchLoginError(error);
          this.settings.disableLoader();
        }
      });
  }

  renewTwoFaCode(token: string, callback: () => void): void {
    this.restService.renewTwoFaCodeRequest(token).subscribe({
      next: response => {
        if (response.status === 200) {
          this.settings.disableLoader();
          callback();
        }
      },
      error: error => {
        this.toastService.showToasterWarning('Ooops qualcosa è andato storto! Effettua nuovamente il login.');
        throwError(error.headers.get(this.config.HEADER_FIELDS.errorMessage));
        this.settings.disableLoader();
      }
    });
  }

  twoFactorAuth(token: string, securityCode: string, saveDeviceTwoFa: boolean): void {
    this.settings.enableLoader();
    this.restService.authTwoFaRequest(token, securityCode, saveDeviceTwoFa).subscribe({
      next: response => {
        if (response.status === 200) {
          this.store.deleteTwoFaRequest();
          this.restService.getUser()
            .subscribe(
              user => {
                if (user.body != null) {
                  this.loginSubscribe(user);
                }
                this.settings.disableLoader();
              });
        }
      },
      error: error => {
        this.toastService.showToasterWarning('Il codice inserito non è valido!');
        throwError(error.headers.get(this.config.HEADER_FIELDS.errorMessage));
        this.settings.disableLoader();
      }
    });
  }

  dispatchNoAuthError(code: string | null, remainingUserAttempt: string | null = null): void {
    if (code) {
      this.showAttemptWarning(code, remainingUserAttempt);
    }
  }

  showAttemptWarning(code: string, remainingUserAttempt: string | null = null): void {
    let errorMessage: string = this.getUnauthorizedMessage(code);
    if (remainingUserAttempt) {
      errorMessage += '\nRimangono ' + remainingUserAttempt + ' tentativi.';
    }
    this.toastService.showToasterWarning(errorMessage);
  }

  isRoleInPermission(role: string): boolean {
    return this.utils.isInArrayString(this.user.permissions, role);
  }

  loginSubscribe(data: HttpResponse<U>): void {
    this.configureUserApp(data);
    if (!this.config.MAINTENANCE) {
      this.routing.navigateToLastPage();
    } else {
      this.routing.navigateTo('/maintenance');
    }
  }

  protected abstract newUserInstance(): U;

  protected abstract addMenu(): void;

  protected abstract setMenuByPermissions(): void;

  protected abstract updateLayout(): void;

  protected abstract updatePreferences(): void;

  protected configureUserApp(data: HttpResponse<U>): void {
    this.setUser(data.body);
    this.isAccountLocked();
    try {
      this.user.preferences.preferencesInitialized = true;
      this.updateLayout();
      this.settings.updateLanguage(this.user.preferences.language);
      this.setMenuByPermissions();
      this.updatePreferences();
      this.setMenu();
    } catch (e) {
      this.setUser(null);
      this.routing.navigateTo('/login');
      this.toastService.showToasterWarning('Impossibile recuperare l\'utenza!');
      throw new Error('Unable to retrieve user!');
    }
  }

  private dispatchLoginError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      const code = error.headers.get(this.config.HEADER_FIELDS.authCodeField);
      this.settings.getError().code = error.status;
      this.settings.getError().authCode = code;
      this.settings.getError().errorMessage = this.getUnauthorizedMessage(code);
      this.dispatchNoAuthError(code);
      this.routing.navigateTo('/login');
    } else if (Math.trunc(error.status / 100) === 4) {
      this.routing.navigateTo('/400');
    } else {
      this.routing.navigateTo('/500');
    }
    throwError(() => error.headers.get(this.config.HEADER_FIELDS.errorMessage));
  }

  private isAccountLocked(): void {
    if (this.user && this.user.accountLocked) {
      this.setUser(null);
      this.routing.navigateTo('/login');
      this.toastService.showToasterWarning('L\'account è stato bloccato!\nSi prega di contattare l\'assistenza.');
      throw new Error('Account locked!');
    }
  }

  private setMenu(): void {
    if (this.menuService.getMenu().length > 0) {
      this.menuService.resetMenu();
    }
    this.addMenu();
  }

  private getUnauthorizedMessage(status: string | null): string {
    switch (status) {
      case '0':
        return 'Nome utente o password errati!';
      case '1':
        return 'E\' stato raggiunto il limite massimo di accessi simultanei!';
      case '2':
        return 'La licenza è scaduta!';
      case '3':
        return 'Account bloccato!';
      case '4':
        return 'Credenziali scadute!';
      case '5':
        return 'Sessione scaduta! Si prega di riloggarsi.';
      case '6':
        return 'Eseguiti troppi tentativi errati!';
      default:
        return '';
    }
  }

}
